import Image from 'next/image';
import Link from 'next/link';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  SimpleGrid,
  useDisclosure,
} from '@chakra-ui/react';

import OpenGraph from '@/components/OpenGraph';
import FullPage from '@/src/components/UI/FullPage';
import FunwooAPI, { PublishedPostEntity, StaticOptionEntity } from '@/swagger';
import React, { useCallback, useMemo, useState } from 'react';
import { MdSort } from 'react-icons/md';
import Flexbox from '@/components/common/Flexbox';
import classNames from 'classnames';
import { IoClose } from 'react-icons/io5';
import dynamic from 'next/dynamic';
import { SUB_CATEGORIES } from '@/constants/article';
import CheckIcon from '../../src/components/feature/CheckIcon';
import ReactIcon from '@/src/components/common/ReactIcon';

const SubCategoryFilter = dynamic(() => import('@/src/components/pages/post/SubCategoryFilter'), {
  ssr: false,
});

type Props = {
  posts: Array<PublishedPostEntity>;
  banner: StaticOptionEntity;
};
export default function PostExposure({ posts, banner }: Props) {
  const [subCategory, setSubCategory] = useState<string>('');

  const displayPosts = useMemo(() => {
    return posts.filter((post) => (!!subCategory ? post.subCategory === subCategory : true));
  }, [posts, subCategory]);

  return (
    <FullPage
      hasPadding
      hasHeaderPlaceHolder
      headerRightSlot={<FilterButton subCategory={subCategory} setSubCategory={setSubCategory} />}
    >
      <OpenGraph
        title='FUNWOO 德載國際不動產 - 趨勢專欄'
        type='blog'
        url='https://funwoo.com.tw/post'
        imageUrl={banner.value}
      />
      <SubCategoryFilter subCategory={subCategory} setSubCategory={setSubCategory} />
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} mt={4}>
        {displayPosts.map((post, index: number) => (
          <Link key={post.id} href={post.foreignUrl || `/post/${encodeURIComponent(post.uuid)}`}>
            <a
              className='flex flex-col mb-4 space-y-4'
              target={post.foreignUrl ? '_blank' : '_self'}
              rel={post.foreignUrl ? 'noreferrer noopener' : ''}
            >
              <div>
                <Image
                  src={post.coverImage}
                  width={400}
                  height={300}
                  layout='responsive'
                  priority={index === 0}
                  objectFit={'cover'}
                />
              </div>
              <div className='text-md text-gray700'>{post.tag}</div>
              <div className='text-xl text-gray700'>{post.title}</div>
              <strong className='text-brand text-lg'>閱覽全文</strong>
            </a>
          </Link>
        ))}
      </SimpleGrid>
    </FullPage>
  );
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  const {
    data: { items: posts },
  } = await FunwooAPI.blog.getPublishedPosts({
    category: 'TREND',
  });
  const { data: banner } = await FunwooAPI.systemApi.getBanner({ page: 'HOMEPAGE' });
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      posts,
      banner,
    },
    revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
  };
};

const FilterButton: React.FC<{
  subCategory: string;
  setSubCategory: (subCategory: string) => void;
}> = ({ setSubCategory, subCategory }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const onItemClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const subCategory = e.currentTarget.getAttribute('data-sub-category') ?? '';
      setSubCategory(subCategory);
    },
    [setSubCategory]
  );

  return (
    <React.Fragment>
      <Flexbox
        as={'button'}
        align={'center'}
        justify={'center'}
        className={classNames('absolute right-3', 'w-6 h-6')}
        onClick={onOpen}
      >
        <ReactIcon icon={MdSort} size={24} />
      </Flexbox>
      <Drawer onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader px={6} py={4} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Flexbox as={'button'} align={'center'} justify={'center'} onClick={onClose}>
              <ReactIcon icon={IoClose} size={24} />
            </Flexbox>
          </DrawerHeader>
          <DrawerBody pt={0} px={4}>
            {SUB_CATEGORIES.map(({ key: sc, label }) => (
              <Flexbox
                key={sc}
                align={'center'}
                justify={'between'}
                className={classNames('space-x-4 py-4')}
                data-sub-category={sc}
                onClick={onItemClick}
              >
                <p className={'text-lg'}>{label}</p>
                <CheckIcon checked={sc === subCategory} />
              </Flexbox>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </React.Fragment>
  );
};
